import React, { useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import { graphThemes } from "../../themes/graphThemes";

const MATH_FONT = "Cambria Math, STIX Two Math, Georgia, 'Times New Roman', serif";
const PURPLE_LIGHT = "#7c3aed";
const ALPHA = "\u03b1";
const BETA = "\u03b2";
const DEG = "\u00b0";
const PARALLEL = "\u2225";
const ANGLE_VALUE_SIZE = 16;
const ANGLE_SYMBOL_SIZE = 18;
const toRad = (deg) => (deg * Math.PI) / 180;

function Label({ x, y, children, fill, size = 22, italic = false, anchor = "middle" }) {
  return (
    <text
      x={x}
      y={y}
      fill={fill}
      fontFamily={MATH_FONT}
      fontSize={size}
      fontStyle={italic ? "italic" : "normal"}
      textAnchor={anchor}
      dominantBaseline="middle"
    >
      {children}
    </text>
  );
}

function AngleArc({ d, color, width = 4 }) {
  return <path d={d} fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" />;
}

function pointAt(cx, cy, angle, distance) {
  const rad = toRad(angle);
  return { x: cx + Math.cos(rad) * distance, y: cy + Math.sin(rad) * distance };
}

function lineThrough(cx, cy, angle, distance) {
  return {
    start: pointAt(cx, cy, angle, distance),
    end: pointAt(cx, cy, angle + 180, distance),
  };
}

function arcPath(cx, cy, radius, startAngle, endAngle) {
  const start = pointAt(cx, cy, radius === 0 ? 0 : startAngle, radius);
  const end = pointAt(cx, cy, radius === 0 ? 0 : endAngle, radius);
  const delta = ((endAngle - startAngle) % 360 + 360) % 360;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 ${delta > 180 ? 1 : 0} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function midArcPoint(cx, cy, radius, startAngle, endAngle) {
  const delta = ((endAngle - startAngle) % 360 + 360) % 360;
  return pointAt(cx, cy, startAngle + delta / 2, radius);
}

function angleSector(point, start, end, color, compact = false) {
  const arcRadius = compact ? 22 : 30;
  const labelRadius = compact ? 34 : 42;
  return {
    arc: <AngleArc d={arcPath(point.x, point.y, arcRadius, start, end)} color={color} />,
    label: midArcPoint(point.x, point.y, labelRadius, start, end),
  };
}

function getColors(theme) {
  const graphStyle = graphThemes[theme === "dark" ? "cyberpunk" : "classic"];
  return {
    text: "var(--color-text, #f8fafc)",
    line: "var(--color-text, #f8fafc)",
    given: "var(--color-accent, #f59e0b)",
    unknown: theme === "dark" ? graphStyle.lineColor : PURPLE_LIGHT,
    fill: "rgba(120, 53, 15, 0.22)",
  };
}

function CrossingLinesDiagram({ data, colors, compact = false, dynamic = false }) {
  const width = compact ? 330 : 430;
  const height = compact ? 170 : 220;
  const showLetters = data.letters === true;
  const given = Number.isFinite(data.given) ? data.given : 86;
  const givenText = showLetters ? BETA : String(given) + DEG;
  const cx = 215;
  const cy = dynamic || showLetters ? 108 : 102;
  const spread = Math.max(34, Math.min(146, given));
  const leftTop = 270 - spread / 2;
  const rightTop = 270 + spread / 2;
  const lineA = lineThrough(cx, cy, leftTop, compact ? 135 : 170);
  const lineB = lineThrough(cx, cy, rightTop, compact ? 135 : 170);
  const topArc = arcPath(cx, cy, compact ? 22 : 26, leftTop, rightTop);
  const bottomArc = arcPath(cx, cy, compact ? 22 : 26, 90 - spread / 2, 90 + spread / 2);
  const topLabel = pointAt(cx, cy, 270, compact ? 42 : 52);
  const bottomLabel = pointAt(cx, cy, 90, compact ? 48 : 58);

  return (
    <svg width={width} height={height} viewBox="0 0 430 220" style={{ maxWidth: "100%", height: "auto", display: "block" }} role="img" aria-label="Kąty utworzone przez przecinające się proste">
      <line x1={lineA.start.x} y1={lineA.start.y} x2={lineA.end.x} y2={lineA.end.y} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <line x1={lineB.start.x} y1={lineB.start.y} x2={lineB.end.x} y2={lineB.end.y} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="5" fill={colors.line} />
      <AngleArc d={topArc} color={colors.given} />
      <AngleArc d={bottomArc} color={colors.unknown} />
      <Label x={topLabel.x} y={topLabel.y} fill={colors.given} size={ANGLE_VALUE_SIZE} italic={showLetters}>{givenText}</Label>
      <Label x={bottomLabel.x} y={bottomLabel.y} fill={colors.unknown} size={ANGLE_SYMBOL_SIZE} italic>{ALPHA}</Label>
    </svg>
  );
}

function AdjacentRatioDiagram({ data, colors, compact = false, dynamic = false }) {
  const width = compact ? 330 : 430;
  const height = compact ? 170 : 220;
  const isGivenAngle = data.mode === "adjacentGiven";
  const showLetters = data.letters === true;
  const cx = 215;
  const cy = compact ? 132 : 154;
  const givenAngle = isGivenAngle ? data.given : 180 - data.answer;
  const given = Math.max(34, Math.min(146, givenAngle));
  const rayAngle = 360 - given;
  const rayEnd = pointAt(cx, cy, rayAngle, compact ? 92 : 115);
  const unknownSpec = angleSector({ x: cx, y: cy }, 180, rayAngle, colors.unknown, compact);
  const givenSpec = angleSector({ x: cx, y: cy }, rayAngle, 360, colors.given, compact);
  const givenText = isGivenAngle ? String(data.given) + DEG : showLetters ? BETA : String(data.factor) + ALPHA;

  return (
    <svg width={width} height={height} viewBox="0 0 430 220" style={{ maxWidth: "100%", height: "auto", display: "block" }} role="img" aria-label="Kąty na prostej">
      <line x1="70" y1={cy} x2="360" y2={cy} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <line x1={cx} y1={cy} x2={rayEnd.x} y2={rayEnd.y} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="5" fill={colors.line} />
      {unknownSpec.arc}
      {givenSpec.arc}
      <Label x={unknownSpec.label.x} y={unknownSpec.label.y} fill={colors.unknown} size={ANGLE_SYMBOL_SIZE} italic>{ALPHA}</Label>
      <Label x={givenSpec.label.x} y={givenSpec.label.y} fill={colors.given} size={ANGLE_VALUE_SIZE} italic={showLetters}>{givenText}</Label>
    </svg>
  );
}

function StraightLineThreeAnglesDiagram({ data, colors, compact = false }) {
  const width = compact ? 330 : 430;
  const height = compact ? 220 : 250;
  const cx = 215;
  const cy = compact ? 110 : 126;
  const factor = Number.isFinite(data.factor) ? data.factor : 2;
  const given = Number.isFinite(data.given) ? data.given : 90;
  const alpha = Number.isFinite(data.answer) ? data.answer : (360 - given) / (factor + 1);
  const startAngle = -112;
  const rayAngles = [
    startAngle,
    startAngle + alpha,
    startAngle + alpha + factor * alpha,
  ];
  const rayLength = compact ? 78 : 98;
  const rayEnds = rayAngles.map((angle) => pointAt(cx, cy, angle, rayLength));
  const unknownSpec = angleSector({ x: cx, y: cy }, rayAngles[0], rayAngles[1], colors.unknown, compact);
  const factorSpec = angleSector({ x: cx, y: cy }, rayAngles[1], rayAngles[2], colors.given, compact);
  const givenSpec = angleSector({ x: cx, y: cy }, rayAngles[2], rayAngles[0] + 360, colors.given, compact);

  return (
    <svg width={width} height={height} viewBox="0 0 430 250" style={{ maxWidth: "100%", height: "auto", display: "block" }} role="img" aria-label="Trzy katy tworzace kat pelny">
      {rayEnds.map((point, index) => (
        <line key={index} x1={cx} y1={cy} x2={point.x} y2={point.y} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      ))}
      <circle cx={cx} cy={cy} r="5" fill={colors.line} />
      {unknownSpec.arc}
      {factorSpec.arc}
      {givenSpec.arc}
      <Label x={unknownSpec.label.x} y={unknownSpec.label.y} fill={colors.unknown} size={ANGLE_SYMBOL_SIZE} italic>{ALPHA}</Label>
      <Label x={factorSpec.label.x} y={factorSpec.label.y} fill={colors.given} size={ANGLE_VALUE_SIZE} italic>{factor}{ALPHA}</Label>
      <Label x={givenSpec.label.x} y={givenSpec.label.y} fill={colors.given} size={ANGLE_VALUE_SIZE}>{given}{DEG}</Label>
    </svg>
  );
}
function ParallelLinesDiagram({ data, colors, compact = false, dynamic = false }) {
  const isCorresponding = data.mode === "corresponding";
  const isAlternate = data.mode === "alternate";
  const showLetters = data.letters === true;
  const layout = compact
    ? {
        width: 330,
        height: 230,
        viewBox: "0 0 420 270",
        topY: 70,
        bottomY: 190,
        topX: 260,
        lineStartX: 50,
        lineEndX: 370,
        k: [342, 48],
        l: [342, 214],
        parallel: [340, 132],
      }
    : {
        width: 470,
        height: 300,
        viewBox: "0 0 470 315",
        topY: 78,
        bottomY: 210,
        topX: 263,
        lineStartX: 56,
        lineEndX: 414,
        k: [392, 50],
        l: [392, 238],
        parallel: [374, 146],
      };

  const given = Number.isFinite(data.given) ? data.given : 60;
  const acute = Math.min(given, 180 - given);
  const lineAngleUpRight = 360 - acute;
  const lineAngleDownLeft = 180 - acute;
  const topPoint = { x: layout.topX, y: layout.topY };
  const dy = layout.bottomY - layout.topY;
  const downRad = toRad(lineAngleDownLeft);
  const bottomPoint = {
    x: layout.topX + (Math.cos(downRad) * dy) / Math.sin(downRad),
    y: layout.bottomY,
  };
  const transLength = compact ? 76 : 88;
  const transTop = pointAt(topPoint.x, topPoint.y, lineAngleUpRight, transLength);
  const transBottom = pointAt(bottomPoint.x, bottomPoint.y, lineAngleDownLeft, transLength);
  const sectors = {
    topUpperRight: [lineAngleUpRight, 360],
    topUpperLeft: [180, lineAngleUpRight],
    topLowerLeft: [lineAngleDownLeft, 180],
    topLowerRight: [0, lineAngleDownLeft],
    bottomUpperRight: [lineAngleUpRight, 360],
    bottomUpperLeft: [180, lineAngleUpRight],
    bottomLowerLeft: [lineAngleDownLeft, 180],
    bottomLowerRight: [0, lineAngleDownLeft],
  };
  const sectorAt = (point, name, color) => {
    const [sectorStart, sectorEnd] = sectors[name];
    return angleSector(point, sectorStart, sectorEnd, color, compact);
  };
  let givenSpec;
  let unknownSpec;
  if (isCorresponding) {
    const sectorName = given <= 90 ? "UpperRight" : "UpperLeft";
    givenSpec = sectorAt(topPoint, "top" + sectorName, colors.given);
    unknownSpec = sectorAt(bottomPoint, "bottom" + sectorName, colors.unknown);
  } else if (isAlternate) {
    if (given <= 90) {
      givenSpec = sectorAt(topPoint, "topLowerLeft", colors.given);
      unknownSpec = sectorAt(bottomPoint, "bottomUpperRight", colors.unknown);
    } else {
      givenSpec = sectorAt(topPoint, "topLowerRight", colors.given);
      unknownSpec = sectorAt(bottomPoint, "bottomUpperLeft", colors.unknown);
    }
  } else if (given <= 90) {
    givenSpec = sectorAt(topPoint, "topLowerLeft", colors.given);
    unknownSpec = sectorAt(bottomPoint, "bottomUpperLeft", colors.unknown);
  } else {
    givenSpec = sectorAt(topPoint, "topLowerRight", colors.given);
    unknownSpec = sectorAt(bottomPoint, "bottomUpperRight", colors.unknown);
  }

  const givenText = showLetters ? BETA : String(given) + DEG;

  return (
    <svg width={layout.width} height={layout.height} viewBox={layout.viewBox} style={{ maxWidth: "100%", height: "auto", display: "block" }} role="img" aria-label="Dwie proste równoległe przecięte trzecią prostą">
      <line x1={layout.lineStartX} y1={layout.topY} x2={layout.lineEndX} y2={layout.topY} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <line x1={layout.lineStartX} y1={layout.bottomY} x2={layout.lineEndX} y2={layout.bottomY} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <line x1={transBottom.x} y1={transBottom.y} x2={transTop.x} y2={transTop.y} stroke={colors.line} strokeWidth="4" strokeLinecap="round" />
      <circle cx={topPoint.x} cy={topPoint.y} r="4" fill={colors.line} />
      <circle cx={bottomPoint.x} cy={bottomPoint.y} r="4" fill={colors.line} />
      <Label x={layout.k[0]} y={layout.k[1]} fill={colors.line} size={22} italic>k</Label>
      <Label x={layout.l[0]} y={layout.l[1]} fill={colors.line} size={22} italic>l</Label>
      <Label x={layout.parallel[0]} y={layout.parallel[1]} fill={colors.given} size={compact ? 15 : 17}>{"k " + PARALLEL + " l"}</Label>
      {givenSpec.arc}
      {unknownSpec.arc}
      <Label x={givenSpec.label.x} y={givenSpec.label.y} fill={colors.given} size={ANGLE_VALUE_SIZE} italic={showLetters}>{givenText}</Label>
      <Label x={unknownSpec.label.x} y={unknownSpec.label.y} fill={colors.unknown} size={ANGLE_SYMBOL_SIZE} italic>{ALPHA}</Label>
    </svg>
  );
}

export default function AngleRelationsDiagram({ data, compact = false, dynamic = false }) {
  const { theme } = useContext(ThemeContext) || {};
  const colors = getColors(theme);
  const content = data.mode === "vertical"
    ? <CrossingLinesDiagram data={data} colors={colors} compact={compact} dynamic={dynamic} />
    : data.mode === "straightLineThreeAngles"
      ? <StraightLineThreeAnglesDiagram data={data} colors={colors} compact={compact} dynamic={dynamic} />
    : data.mode === "adjacentRatio" || data.mode === "adjacentGiven"
      ? <AdjacentRatioDiagram data={data} colors={colors} compact={compact} dynamic={dynamic} />
      : <ParallelLinesDiagram data={data} colors={colors} compact={compact} dynamic={dynamic} />;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: compact ? "8px 0 12px" : "18px 0 20px", width: "100%", overflow: "hidden" }}>
      {content}
    </div>
  );
}
